import { useEffect, useState } from 'react'
import { useRetorikStore } from '../Contexts/RetorikContext'
import { useUtilsStore } from '../Contexts/UtilsContext'
import { useViewStore } from '../Contexts/ViewContext'
import { useLocaleStore } from '../Contexts/LocaleContext'
import { useDirectlineStore } from '../Contexts/DirectlineContext'
import { useSpeechStore } from '../Contexts/SpeechContext'
import { UserData } from '../../models/types'

interface SendActivityEventListenerProps {
  isRetorikNews: boolean
  hasConversationCookie: boolean
  userData: UserData
}

interface EventReceived {
  type: EventsReceived
  detail?: any
}

enum EventsReceived {
  'retorikSendActivity' = 1,
  'retorikSendEvent',
  'retorikStartListening',
  'retorikDisplaySubtitles',
  'retorikSetVisibility'
}

const SendActivityEventListener = ({ isRetorikNews, hasConversationCookie, userData }: SendActivityEventListenerProps): null => {
  const locale = useLocaleStore((state) => state.locale)
  const activityToSend = useUtilsStore((state) => state.activityToSend)
  const displayGDPR = useUtilsStore((state) => state.GDPRDisplay)
  const userConsent = useUtilsStore((state) => state.GDPRUserConsent)
  const sendUtilActivity = useUtilsStore((state) => state.actions.sendActivity)
  const loaderClosed = useRetorikStore((state) => state.loaderClosed)
  const appAvailable = useRetorikStore((state) => state.appAvailable)
  const skipWelcome = useRetorikStore((state) => state.configuration.skipWelcome)
  const userAlreadySet = useRetorikStore((state) => state.userAlreadySet)
  const isMicrophoneAllowed = useSpeechStore((state) => state.isMicrophoneAllowed)
  const characterLoaded = useViewStore((state) => state.characterLoaded)
  const setVisibility = useViewStore((state) => state.actions.setVisibility)
  const askPermission = useSpeechStore((state) => state.actions.askPermission)
  const { setDisplaySubtitles, setUserAlreadySet } = useRetorikStore((state) => state.actions)
  const toggleMicrophone = useSpeechStore((state) => state.actions.toggleMicrophone)
  const { sendMessage, sendEvent } = useDirectlineStore((state) => state.actions)

  const [eventReceived, setEventReceived] = useState<EventReceived | null>(null)

  useEffect(() => {
    if (eventReceived) {
      try {
        switch (eventReceived.type) {
          case EventsReceived.retorikSendActivity:
            eventReceived.detail && sendMessage(undefined, eventReceived.detail)
            break
          case EventsReceived.retorikDisplaySubtitles:
            setDisplaySubtitles(!!eventReceived.detail)
            break
          case EventsReceived.retorikStartListening:
            isMicrophoneAllowed ? toggleMicrophone() : askPermission()
            break
          case EventsReceived.retorikSendEvent:
            eventReceived.detail && sendEvent(eventReceived.detail.name, eventReceived.detail.value)
            break
          case EventsReceived.retorikSetVisibility:
            eventReceived.detail && setVisibility(eventReceived.detail)
            break
        }
      } catch (e) {
        console.warn('Retorik Framework > error on event reception')
      }
    }
  }, [eventReceived])

  const handleEvents = (event: CustomEvent): void => {
    setEventReceived({
      type: EventsReceived[event.type],
      detail: event.detail
    })
  }

  useEffect(() => {
    document.addEventListener('retorikSendActivity', handleEvents)
    document.addEventListener('retorikSendEvent', handleEvents)
    document.addEventListener('retorikStartListening', handleEvents)
    document.addEventListener('retorikDisplaySubtitles', handleEvents)
    document.addEventListener('retorikSetVisibility', handleEvents)

    return (): void => {
      document.removeEventListener('retorikSendActivity', handleEvents)
      document.removeEventListener('retorikStartListening', handleEvents)
      document.removeEventListener('retorikDisplaySubtitles', handleEvents)
      document.removeEventListener('retorikSetVisibility', handleEvents)
    }
  }, [])

  /**
   * Send mandatory events : setUser to start conversation, Davi.GDPRConsent for GDPR module
   */
  const sendMandatoryEvents = async (): Promise<void> => {
    sendEvent(
      'Davi.GDPRConsent',
      {
        userConsent: userConsent
      },
      () =>
        sendEvent('setUser', {
          user: { ...userData, gdprConsent: true },
          skipWelcome: !!skipWelcome
        })
    )
  }

  useEffect(() => {
    if (isRetorikNews || hasConversationCookie) {
      setUserAlreadySet(true)
    } else if (!userAlreadySet && appAvailable && !displayGDPR && characterLoaded) {
      setUserAlreadySet(true)
      sendMandatoryEvents()
    }
  }, [appAvailable, loaderClosed, displayGDPR, characterLoaded])

  useEffect(() => {
    if (activityToSend) {
      if (activityToSend.type === 'message' && activityToSend.text && typeof activityToSend.text !== 'string') {
        const tempActivity = { ...activityToSend }
        tempActivity.text = activityToSend.text[locale] || ''
        sendMessage(tempActivity.text)
      } else {
        sendMessage(undefined, activityToSend.value || activityToSend)
      }

      sendUtilActivity(undefined)
    }
  }, [activityToSend])

  return null
}

export default SendActivityEventListener
